import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

// Mirrors the detection logic in `Navbar.tsx`: the new landing page wraps
// content in `[data-theme="light"]`, while legacy/platform views keep the
// dark theme. Watching both `<html>.classList` and any `data-theme="light"`
// node lets the dropdown re-skin live as the user navigates without us
// needing to thread a prop down from every parent.
const computeIsLight = () =>
  document.documentElement.classList.contains('light') ||
  !!document.querySelector('[data-theme="light"]');

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isLight, setIsLight] = useState<boolean>(() => {
    if (typeof document === 'undefined') return false;
    return computeIsLight();
  });

  useEffect(() => {
    const update = () => setIsLight(computeIsLight());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });
    return () => observer.disconnect();
  }, []);

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  // Trigger button — adapt text colour so it reads on the light landing
  // hero (otherwise white-on-white). Hover keeps the brand-mint accent
  // in both themes for continuity with the legacy navbar look.
  const triggerClass = isLight
    ? 'text-[#0F1114] hover:text-[#5bc9c1] hover:bg-transparent py-2 flex items-center gap-2 transition-colors font-normal tracking-[-0.011em]'
    : 'text-white hover:text-[#6dd8d0] hover:bg-transparent py-2 flex items-center gap-2 transition-colors font-normal tracking-[-0.011em]';

  // Dropdown panel — light theme uses the same surface treatment as the
  // landing-new desktop nav sub-menu (white card, hairline border, soft
  // teal-tinted shadow, ink text, pale-mint hover). Dark theme keeps the
  // pre-existing pivot-dark styling.
  const contentClass = isLight
    ? 'bg-white border-black/10 min-w-[150px] z-[150] shadow-[0_18px_40px_-16px_rgba(6,68,74,0.18),0_4px_12px_-6px_rgba(6,68,74,0.10)]'
    : 'bg-pivot-dark border-white/20 min-w-[150px] z-[150]';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={triggerClass}
          style={{ fontFamily: 'var(--font-sans)', fontSize: '18px' }}
        >
          <Globe className="h-5 w-5" />
          <span className="font-normal">{currentLanguage.code.toUpperCase()}</span>
          <span className="sr-only">Change language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={contentClass}>
        {languages.map((lang) => {
          const isActive = currentLanguage.code === lang.code;
          const itemClass = isLight
            ? `cursor-pointer transition-colors ${
                isActive
                  ? 'bg-[#E6F4F2] text-[#0a4f4a]'
                  : 'text-[#0F1114] hover:bg-[#E6F4F2] hover:text-[#0a4f4a]'
              }`
            : `cursor-pointer transition-colors ${
                isActive
                  ? 'bg-[#5bc9c1]/20 text-[#5bc9c1]'
                  : 'text-white hover:bg-white/10 hover:text-[#5bc9c1]'
              }`;
          const checkClass = isLight ? 'text-[#0a4f4a] ml-2' : 'text-[#5bc9c1] ml-2';
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={itemClass}
            >
              <span className="flex items-center justify-between w-full">
                <span className="font-medium">{lang.nativeName}</span>
                {isActive && <span className={checkClass}>✓</span>}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
